import React from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import styles from './agent-help-modal.css';

const agentUrl = process.env.AIPNT_DEVICE_AGENT_URL || 'http://localhost:20559';
const hardwareUrl = process.env.HARDWARE_AGENT_URL || 'http://localhost:20557';

class AgentHelpModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            activeTab: 'windows',
            deviceStatus: 'checking', // 'online' | 'offline' | 'checking'
            hardwareStatus: 'checking',
            deviceDetails: null,
            hardwareDetails: null,
            checking: false,
            copiedIndex: null
        };

        this.checkStatuses = this.checkStatuses.bind(this);
        this.copyToClipboard = this.copyToClipboard.bind(this);
    }

    componentDidMount() {
        this.checkStatuses();
    }

    async checkStatuses() {
        this.setState({ checking: true });

        // Check Device Agent (Port 20559)
        let deviceStatus = 'offline';
        let deviceDetails = null;
        try {
            const res = await fetch(`${agentUrl}/v1/identity`, { signal: AbortSignal.timeout(3000) });
            if (res.ok) {
                deviceDetails = await res.json();
                deviceStatus = 'online';
            }
        } catch (_err) {
            deviceStatus = 'offline';
        }

        // Check Hardware Agent (Port 20557)
        let hardwareStatus = 'offline';
        let hardwareDetails = null;
        try {
            const res = await fetch(`${hardwareUrl}/compile/ports`, { signal: AbortSignal.timeout(3000) });
            if (res.ok) {
                hardwareDetails = await res.json();
                hardwareStatus = 'online';
            }
        } catch (_err) {
            // Also try /health
            try {
                const res2 = await fetch(`${hardwareUrl}/health`, { signal: AbortSignal.timeout(3000) });
                if (res2.ok) {
                    hardwareStatus = 'online';
                }
            } catch (_err2) {
                hardwareStatus = 'offline';
            }
        }

        this.setState({
            deviceStatus,
            hardwareStatus,
            deviceDetails,
            hardwareDetails,
            checking: false
        });
    }

    copyToClipboard(text, index) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text);
            this.setState({ copiedIndex: index });
            setTimeout(() => this.setState({ copiedIndex: null }), 2000);
        }
    }

    renderWindowsTab() {
        return (
            <div className={styles.tabPane}>
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>⚡ Prarambh Agents Installer</h3>
                    <p className={styles.sectionDesc}>
                        This installer sets up and runs the background agents required for hardware integration.
                    </p>

                    <div className={styles.stepList}>
                        <div className={styles.stepItem}>
                            <div className={styles.stepNumber}>1</div>
                            <div className={styles.stepContent}>
                                <div className={styles.stepTitle}>Download</div>
                                <div className={styles.stepText}>
                                    Download the latest <strong>.zip</strong> file from the <a href="https://github.com/Hpdjoy/Prarambh-agents-Installer" target="_blank" rel="noopener noreferrer" style={{color: 'var(--brand-light)'}}>Prarambh Agents Installer GitHub repository</a>.
                                </div>
                            </div>
                        </div>

                        <div className={styles.stepItem}>
                            <div className={styles.stepNumber}>2</div>
                            <div className={styles.stepContent}>
                                <div className={styles.stepTitle}>Extract</div>
                                <div className={styles.stepText}>
                                    Right-click the downloaded <code>.zip</code> file and select:<br/>
                                    <strong>Extract All → Extract</strong>
                                </div>
                            </div>
                        </div>

                        <div className={styles.stepItem}>
                            <div className={styles.stepNumber}>3</div>
                            <div className={styles.stepContent}>
                                <div className={styles.stepTitle}>Run the Installer</div>
                                <div className={styles.stepText}>
                                    Open the extracted folder and double-click:<br/>
                                    <strong>Setup.exe</strong>
                                </div>
                            </div>
                        </div>
                        
                        <div className={styles.stepItem}>
                            <div className={styles.stepNumber}>4</div>
                            <div className={styles.stepContent}>
                                <div className={styles.stepTitle}>Complete Installation</div>
                                <div className={styles.stepText}>
                                    Follow the instructions shown by the installer. Once the installation is complete, the Prarambh Agents will be installed and ready to use.
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.noteBox} style={{ marginTop: '1rem' }}>
                        📋 <strong>Requirements:</strong> Windows 10 or Windows 11, Internet connection. Administrator permission may be required during installation.
                    </div>
                </div>
            </div>
        );
    }

    renderManualTab() {
        const cmdDevice = `cd "AIPNT Device Agent"\nnpm install\nnpm start`;
        const cmdHardware = `cd "AIPNT Hardware Agent"\nnpm install\nnpm start`;
        const cmdPm2 = `npm install -g pm2\npm2 start "AIPNT Device Agent/src/index.js" --name "AIPNT-Device-Agent"\npm2 start "AIPNT Hardware Agent/src/index.js" --name "AIPNT-Hardware-Agent"\npm2 save`;

        return (
            <div className={styles.tabPane}>
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>💻 Manual / Developer Setup (Node.js)</h3>
                    <p className={styles.sectionDesc}>
                        If you are on Mac, Linux, or developing locally, you can start the agents manually with Node.js 20+.
                    </p>

                    <div className={styles.stepList}>
                        <div className={styles.stepItem}>
                            <div className={styles.stepNumber}>1</div>
                            <div className={styles.stepContent}>
                                <div className={styles.stepTitle}>Start Device Agent (Port 20559)</div>
                                <div className={styles.stepText}>
                                    Handles hardware device cryptographic signature & license authentication:
                                </div>
                                <div className={styles.codeBlock}>
                                    <span className={styles.codeText}>{cmdDevice}</span>
                                    <button
                                        type="button"
                                        className={`${styles.copyBtn} ${this.state.copiedIndex === 'man1' ? styles.copyBtnCopied : ''}`}
                                        onClick={() => this.copyToClipboard(cmdDevice, 'man1')}
                                    >
                                        {this.state.copiedIndex === 'man1' ? '✓ Copied' : 'Copy'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className={styles.stepItem}>
                            <div className={styles.stepNumber}>2</div>
                            <div className={styles.stepContent}>
                                <div className={styles.stepTitle}>Start Hardware Agent (Port 20557)</div>
                                <div className={styles.stepText}>
                                    Handles Arduino compiling and USB serial flashing:
                                </div>
                                <div className={styles.codeBlock}>
                                    <span className={styles.codeText}>{cmdHardware}</span>
                                    <button
                                        type="button"
                                        className={`${styles.copyBtn} ${this.state.copiedIndex === 'man2' ? styles.copyBtnCopied : ''}`}
                                        onClick={() => this.copyToClipboard(cmdHardware, 'man2')}
                                    >
                                        {this.state.copiedIndex === 'man2' ? '✓ Copied' : 'Copy'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className={styles.stepItem}>
                            <div className={styles.stepNumber}>3</div>
                            <div className={styles.stepContent}>
                                <div className={styles.stepTitle}>Optional: Keep Running in Background with PM2</div>
                                <div className={styles.codeBlock}>
                                    <span className={styles.codeText}>{cmdPm2}</span>
                                    <button
                                        type="button"
                                        className={`${styles.copyBtn} ${this.state.copiedIndex === 'man3' ? styles.copyBtnCopied : ''}`}
                                        onClick={() => this.copyToClipboard(cmdPm2, 'man3')}
                                    >
                                        {this.state.copiedIndex === 'man3' ? '✓ Copied' : 'Copy'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderHardwareTab() {
        const cmdArduinoCli = `arduino-cli core install arduino:avr`;

        return (
            <div className={styles.tabPane}>
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>🤖 Arduino & Robot Board Prerequisites</h3>
                    <p className={styles.sectionDesc}>
                        To compile code and upload programs directly onto Arduino Uno, Nano, Mega, Raspberry Pi Pico, or ESP32 boards:
                    </p>

                    <div className={styles.stepList}>
                        <div className={styles.stepItem}>
                            <div className={styles.stepNumber}>1</div>
                            <div className={styles.stepContent}>
                                <div className={styles.stepTitle}>Install arduino-cli on the System PATH</div>
                                <div className={styles.stepText}>
                                    Ensure <code>arduino-cli</code> is installed and accessible from the command line.
                                </div>
                            </div>
                        </div>

                        <div className={styles.stepItem}>
                            <div className={styles.stepNumber}>2</div>
                            <div className={styles.stepContent}>
                                <div className={styles.stepTitle}>Install the Arduino AVR Core</div>
                                <div className={styles.stepText}>
                                    Run the following command in terminal once:
                                </div>
                                <div className={styles.codeBlock}>
                                    <span className={styles.codeText}>{cmdArduinoCli}</span>
                                    <button
                                        type="button"
                                        className={`${styles.copyBtn} ${this.state.copiedIndex === 'hard1' ? styles.copyBtnCopied : ''}`}
                                        onClick={() => this.copyToClipboard(cmdArduinoCli, 'hard1')}
                                    >
                                        {this.state.copiedIndex === 'hard1' ? '✓ Copied' : 'Copy'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className={styles.stepItem}>
                            <div className={styles.stepNumber}>3</div>
                            <div className={styles.stepContent}>
                                <div className={styles.stepTitle}>Connect Board via USB</div>
                                <div className={styles.stepText}>
                                    Plug in your Arduino board. When you click <strong>Connect</strong> in the coding editor, select the matching USB Serial Port from the browser pop-up.
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.noteBox} style={{ marginTop: '1rem' }}>
                        🔌 <strong>Supported Boards:</strong> Arduino Uno (ATmega328P), Arduino Nano, Arduino Mega (ATmega2560), ESP32 (MicroPython), and Raspberry Pi Pico.
                    </div>
                </div>
            </div>
        );
    }

    renderFaqTab() {
        return (
            <div className={styles.tabPane}>
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>❓ Frequently Asked Questions & Troubleshooting</h3>

                    <div className={styles.faqItem}>
                        <div className={styles.faqQuestion}>🔴 "This computer has not been activated" error?</div>
                        <div className={styles.faqAnswer}>
                            Make sure the <strong>AIPNT Device Agent</strong> is running on port 20559. If it is running for the first time, enter an Activation Code provided by your system administrator to pair this PC with the cloud.
                        </div>
                    </div>

                    <div className={styles.faqItem}>
                        <div className={styles.faqQuestion}>🔴 "Cannot reach the local compile server"?</div>
                        <div className={styles.faqAnswer}>
                            Make sure the <strong>AIPNT Hardware Agent</strong> is running on port 20557. Start it via the 1-Click Installer or run <code>npm start</code> in the Hardware Agent directory.
                        </div>
                    </div>

                    <div className={styles.faqItem}>
                        <div className={styles.faqQuestion}>🔒 "Access Denied" or COM Port busy during upload?</div>
                        <div className={styles.faqAnswer}>
                            Ensure that the Arduino IDE Serial Monitor or another terminal application is closed so the serial port is free for the AIPNT Platform.
                        </div>
                    </div>

                    <div className={styles.faqItem}>
                        <div className={styles.faqQuestion}>🌐 Does the agent require internet access?</div>
                        <div className={styles.faqAnswer}>
                            The local agents run entirely on <code>localhost</code> (127.0.0.1) on the student computer. Only the login and licensing challenge verification communicate with the cloud backend.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { deviceStatus, hardwareStatus, checking, activeTab, deviceDetails } = this.state;

        return (
            <ReactModal
                isOpen
                className={styles.modalContent}
                overlayClassName={styles.modalOverlay}
                contentLabel="Agent Installation Guide"
                onRequestClose={this.props.onRequestClose}
                ariaHideApp={false}
                style={{
                    overlay: {
                        backgroundColor: 'rgba(5, 5, 12, 0.85)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 10000,
                        padding: '1rem'
                    },
                    content: {
                        position: 'static',
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        overflow: 'visible',
                        inset: 'auto'
                    }
                }}
            >
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerTitleGroup}>
                        <div className={styles.headerIcon}>🛠️</div>
                        <div>
                            <h2 className={styles.title}>Agent Setup & Installation Guide</h2>
                            <p className={styles.subtitle}>Configure local classroom services for device authentication and hardware compiling</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={this.props.onRequestClose}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                {/* Live Status Bar */}
                <div className={styles.statusBar}>
                    <div className={`${styles.statusCard} ${deviceStatus === 'online' ? styles.statusCardOnline : styles.statusCardOffline}`}>
                        <div className={styles.statusInfo}>
                            <span className={styles.statusName}>AIPNT Device Agent</span>
                            <span className={styles.statusPort}>Port 20559</span>
                        </div>
                        <div className={`${styles.statusBadge} ${deviceStatus === 'online' ? styles.badgeOnline : styles.badgeOffline}`}>
                            <span className={`${styles.badgeDot} ${deviceStatus === 'online' ? styles.dotOnline : styles.dotOffline}`} />
                            {deviceStatus === 'online' ? 'Online' : 'Offline'}
                        </div>
                    </div>

                    <div className={`${styles.statusCard} ${hardwareStatus === 'online' ? styles.statusCardOnline : styles.statusCardOffline}`}>
                        <div className={styles.statusInfo}>
                            <span className={styles.statusName}>AIPNT Hardware Agent</span>
                            <span className={styles.statusPort}>Port 20557</span>
                        </div>
                        <div className={`${styles.statusBadge} ${hardwareStatus === 'online' ? styles.badgeOnline : styles.badgeOffline}`}>
                            <span className={`${styles.badgeDot} ${hardwareStatus === 'online' ? styles.dotOnline : styles.dotOffline}`} />
                            {hardwareStatus === 'online' ? 'Online' : 'Offline'}
                        </div>
                    </div>

                    <button
                        type="button"
                        className={styles.refreshBtn}
                        onClick={this.checkStatuses}
                        disabled={checking}
                    >
                        <span className={checking ? styles.refreshSpinner : ''}>🔄</span>
                        {checking ? 'Checking…' : 'Check Status'}
                    </button>
                </div>

                {/* Navigation Tabs */}
                <div className={styles.navTabs}>
                    <button
                        type="button"
                        className={`${styles.navTab} ${activeTab === 'windows' ? styles.navTabActive : ''}`}
                        onClick={() => this.setState({ activeTab: 'windows' })}
                    >
                        🪟 Windows 1-Click Setup
                    </button>
                    <button
                        type="button"
                        className={`${styles.navTab} ${activeTab === 'manual' ? styles.navTabActive : ''}`}
                        onClick={() => this.setState({ activeTab: 'manual' })}
                    >
                        💻 Manual Node.js Setup
                    </button>
                    <button
                        type="button"
                        className={`${styles.navTab} ${activeTab === 'hardware' ? styles.navTabActive : ''}`}
                        onClick={() => this.setState({ activeTab: 'hardware' })}
                    >
                        🤖 Arduino & USB Hardware
                    </button>
                    <button
                        type="button"
                        className={`${styles.navTab} ${activeTab === 'faq' ? styles.navTabActive : ''}`}
                        onClick={() => this.setState({ activeTab: 'faq' })}
                    >
                        ❓ Troubleshooting
                    </button>
                </div>

                {/* Content Body */}
                <div className={styles.bodyContent}>
                    {activeTab === 'windows' && this.renderWindowsTab()}
                    {activeTab === 'manual' && this.renderManualTab()}
                    {activeTab === 'hardware' && this.renderHardwareTab()}
                    {activeTab === 'faq' && this.renderFaqTab()}
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <button
                        type="button"
                        className={styles.primaryBtn}
                        onClick={this.props.onRequestClose}
                    >
                        Close Guide
                    </button>
                </div>
            </ReactModal>
        );
    }
}

AgentHelpModal.propTypes = {
    onRequestClose: PropTypes.func.isRequired
};

export default AgentHelpModal;
